import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Button } from "@progress/kendo-react-buttons";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useDealContext } from "src/dm/components/DealLayout/DealLayout";
import { useDealItems } from "src/dm/hooks/useDealItems";
import {
  fetchBulkOverrides,
  fetchBulkPromoGroups,
  fetchBulkStores,
  fetchBulkZones,
} from "src/dm/hooks/useDealItemBulk";
import CleanDatePicker from "src/dm/components/CleanDatePicker/CleanDatePicker";
import {
  MOVEMENT_CODES,
  useApVendors,
  useBusinessUnits,
  useCatMgrs,
  useEntryTypes,
  useUnitsOfMeasure,
} from "src/dm/hooks/useLookups";
import { useApVendorAddressSequences } from "src/dm/hooks/useScanbackBillingDefaults";
import {
  useBulkEditItems,
  type ApplyScope,
  type BulkEditPayload,
} from "src/dm/hooks/useBulkEditItems";
import type { DealItem } from "src/dm/interfaces/DealItem";
import * as s from "./BulkEditItems.css";

/**
 * BulkEditItems — replaces legacy `Item/BulkEditItems.aspx`.
 *
 * Routing contract (registered in routesConfig):
 *   /deals/:dealId/items/bulk-edit?ids=1,2,3
 *
 * The selected `dealItemId`s arrive as a comma-separated `?ids=` query
 * param (set by the DealItems "Apply to All → Go" button). We carry
 * them through the URL (not sessionStorage like the legacy page) so
 * refresh / back-button / direct-link all behave predictably.
 *
 * Layout mirrors the seven legacy panels:
 *   1. Selected items header
 *   2. Allowances (OI / BB / CRC / PT / RFLCT)
 *   3. Shipper Dates (Buy/Order First+Last, First+Last Ship)
 *   4. Vendor Scanback (amount + Business Unit / Movement / Cat-Mgr /
 *      Entry Type / AP-Vendor → Address-Seq cascade / Invoice Desc /
 *      Default checkbox)
 *   5. Internal Scanback (amount + Movement)
 *   6. Promo Price (gated by a master "Update promo pricing" checkbox)
 *   7. Item Attributes (Projected Units + UOM, Exclude Ad Plan, Exclude
 *      Store Ad Plan)
 *   8. Apply Scope (Add/Update All vs. Specific — four multi-selects
 *      populated from the union of values on the selected items)
 *   9. Sticky action bar — Update / Reset / Cancel
 *
 * Each card has its own "Apply this section?" checkbox so the user can
 * leave whole sections empty (legacy parity — the page sends only the
 * panels you actually populated). On submit we fan out per-item POSTs
 * via `useBulkEditItems`, which handles chunking + serialization.
 */

interface SectionToggle {
  allowance: boolean;
  shipperDates: boolean;
  vendorScan: boolean;
  internalScan: boolean;
  promoPrice: boolean;
  itemAttributes: boolean;
}

interface AllowanceForm {
  offInvoiceAmount: number | null;
  billBackAmount: number | null;
  countRecountAmount: number | null;
  passThruAmount: number | null;
  reflectAmount: number | null;
}

interface ShipperDateForm {
  buyOrderFirst: Date | null;
  buyOrderLast: Date | null;
  firstShip: Date | null;
  lastShip: Date | null;
}

interface VendorScanForm {
  amount: number | null;
  businessUnit: string;
  movementCode: string;
  catMgrCode: string;
  entryType: string;
  apVendorNumber: string;
  addressSequence: number | null;
  invoiceDescription: string;
  setAsDefault: boolean;
}

interface InternalScanForm {
  amount: number | null;
  movementCode: string;
}

interface PromoPriceForm {
  multiUnitCount: number | null;
  price: number | null;
  amountOff: number | null;
  percentOff: number | null;
  specialPricing: string;
}

interface ItemAttributesForm {
  projectedUnits: number | null;
  projectedUnitsUom: string;
  excludeAdPlan: string; // "", "Yes", "No"
  excludeStoreAdPlan: string; // "", "Yes", "No"
}

const SPECIAL_PRICING_OPTIONS = ["", "BOGO", "TROGO"] as const;
const YES_NO_OPTIONS = ["", "Yes", "No"] as const;
const PLACEHOLDER = { code: "", label: "(no change)" } as const;

const parseIds = (raw: string | null): number[] => {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
};

const toIsoDate = (d: Date | null | undefined): string | null => {
  if (!d) return null;
  // Match the existing DealItem date strings: yyyy-MM-ddT00:00:00.
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T00:00:00`;
};

const BulkEditItems = () => {
  const deal = useDealContext();
  const navigate = useNavigate();
  const params = useParams();
  const dealIdFromParams = Number(params.dealId);
  // Belt-and-suspenders — useDealContext provides the loaded deal, but
  // the route param is what React Router actually matched on.
  const dealId = Number.isFinite(deal.dealId) ? deal.dealId : dealIdFromParams;
  const [searchParams] = useSearchParams();
  const selectedIds = useMemo(
    () => parseIds(searchParams.get("ids")),
    [searchParams]
  );

  // Item metadata for the header chip list + the bulk PUT merge step.
  const { data: dealItems } = useDealItems(dealId);
  const itemLookup = useMemo(() => {
    const m = new Map<number, DealItem>();
    (dealItems ?? []).forEach((i) => m.set(i.dealItemId, i));
    return m;
  }, [dealItems]);
  const selectedItems = useMemo(
    () =>
      selectedIds
        .map((id) => itemLookup.get(id))
        .filter((i): i is DealItem => !!i),
    [selectedIds, itemLookup]
  );

  // ----- Section enable toggles -----------------------------------------
  const [enabled, setEnabled] = useState<SectionToggle>({
    allowance: false,
    shipperDates: false,
    vendorScan: false,
    internalScan: false,
    promoPrice: false,
    itemAttributes: false,
  });

  // ----- Form state -----------------------------------------------------
  const [allowance, setAllowance] = useState<AllowanceForm>({
    offInvoiceAmount: null,
    billBackAmount: null,
    countRecountAmount: null,
    passThruAmount: null,
    reflectAmount: null,
  });
  const [shipperDates, setShipperDates] = useState<ShipperDateForm>({
    buyOrderFirst: null,
    buyOrderLast: null,
    firstShip: null,
    lastShip: null,
  });
  const [vendorScan, setVendorScan] = useState<VendorScanForm>({
    amount: null,
    businessUnit: "",
    movementCode: "",
    catMgrCode: "",
    entryType: "",
    apVendorNumber: "",
    addressSequence: null,
    invoiceDescription: "",
    setAsDefault: true,
  });
  const [internalScan, setInternalScan] = useState<InternalScanForm>({
    amount: null,
    movementCode: "",
  });
  const [promoPrice, setPromoPrice] = useState<PromoPriceForm>({
    multiUnitCount: null,
    price: null,
    amountOff: null,
    percentOff: null,
    specialPricing: "",
  });
  const [itemAttributes, setItemAttributes] = useState<ItemAttributesForm>({
    projectedUnits: null,
    projectedUnitsUom: "",
    excludeAdPlan: "",
    excludeStoreAdPlan: "",
  });

  // ----- Apply scope ----------------------------------------------------
  const [scopeMode, setScopeMode] = useState<"all" | "specific">("all");
  const [pickedZones, setPickedZones] = useState<Set<number>>(new Set());
  const [pickedOverrides, setPickedOverrides] = useState<Set<string>>(
    new Set()
  );
  const [pickedStores, setPickedStores] = useState<Set<number>>(new Set());
  const [pickedGroups, setPickedGroups] = useState<Set<number>>(new Set());

  // Apply-scope option lists are loaded once we know the selected ids.
  // Each list is the union of distinct values across the selected items.
  const [scopeOptions, setScopeOptions] = useState<{
    zones: number[];
    overrides: { zoneNumber: number; overrideNumber: number }[];
    stores: number[];
    promoGroups: number[];
    loading: boolean;
    error: string | null;
  }>({
    zones: [],
    overrides: [],
    stores: [],
    promoGroups: [],
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (selectedIds.length === 0) return;
    let cancelled = false;
    (async () => {
      setScopeOptions((s) => ({ ...s, loading: true, error: null }));
      try {
        const [zones, overrides, stores, groups] = await Promise.all([
          fetchBulkZones(dealId, selectedIds),
          fetchBulkOverrides(dealId, selectedIds),
          fetchBulkStores(dealId, selectedIds),
          fetchBulkPromoGroups(dealId, selectedIds),
        ]);
        if (cancelled) return;
        setScopeOptions({
          zones,
          overrides,
          stores,
          promoGroups: groups,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setScopeOptions({
          zones: [],
          overrides: [],
          stores: [],
          promoGroups: [],
          loading: false,
          error: err instanceof Error ? err.message : "Lookup failed",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
    // selectedIds is parsed from the URL once; identity changes only when
    // the user navigates to a different ids list.
  }, [dealId, selectedIds]);

  // ----- Lookup data ----------------------------------------------------
  const businessUnits = useBusinessUnits();
  const movementCodes = MOVEMENT_CODES;
  const catMgrs = useCatMgrs();
  const entryTypes = useEntryTypes();
  const uoms = useUnitsOfMeasure();
  const apVendors = useApVendors(deal.vendorBrokerId);
  const addressSequences = useApVendorAddressSequences(
    vendorScan.apVendorNumber || undefined
  );

  // ----- Save -----------------------------------------------------------
  const bulkEdit = useBulkEditItems(dealId);
  const [result, setResult] = useState<{
    total: number;
    succeeded: number;
    failed: number;
    errors: { dealItemId: number; section: string; message: string }[];
  } | null>(null);
  const [topLevelError, setTopLevelError] = useState<string | null>(null);

  const itemsListLabel = `${selectedItems.length || selectedIds.length} item${selectedIds.length === 1 ? "" : "s"} selected`;
  const titleSuffix =
    selectedIds.length > 0
      ? `Bulk Edit ${selectedIds.length} Items`
      : "Bulk Edit Items";

  const hasAnySectionEnabled =
    enabled.allowance ||
    enabled.shipperDates ||
    enabled.vendorScan ||
    enabled.internalScan ||
    enabled.promoPrice ||
    enabled.itemAttributes;

  const buildPayload = (): BulkEditPayload | null => {
    const payload: BulkEditPayload = { dealItemIds: selectedIds };

    if (enabled.allowance) {
      const a = allowance;
      const anyAmt =
        a.offInvoiceAmount !== null ||
        a.billBackAmount !== null ||
        a.countRecountAmount !== null ||
        a.passThruAmount !== null ||
        a.reflectAmount !== null;
      if (!anyAmt) {
        setTopLevelError(
          "Allowance section is enabled but no amounts were entered."
        );
        return null;
      }
      payload.allowance = {
        costZoneNumber: 0,
        offInvoiceAmount: a.offInvoiceAmount,
        billBackAmount: a.billBackAmount,
        countRecountAmount: a.countRecountAmount,
        passThruAmount: a.passThruAmount,
        reflectAmount: a.reflectAmount,
      };
    }

    if (enabled.shipperDates) {
      const d = shipperDates;
      if (!d.buyOrderFirst && !d.buyOrderLast && !d.firstShip && !d.lastShip) {
        setTopLevelError(
          "Shipper Dates section is enabled but no dates were entered."
        );
        return null;
      }
      payload.shipperDates = {
        shipperFirstOrderDate: toIsoDate(d.buyOrderFirst),
        shipperLastOrderDate: toIsoDate(d.buyOrderLast),
        shipperFirstShipDate: toIsoDate(d.firstShip),
        shipperLastShipDate: toIsoDate(d.lastShip),
      };
    }

    if (enabled.vendorScan) {
      if (!vendorScan.amount || vendorScan.amount <= 0) {
        setTopLevelError(
          "Vendor Scanback is enabled but no amount was entered."
        );
        return null;
      }
      payload.vendorScan = {
        zoneNumber: 0,
        amount: vendorScan.amount,
        movementCode: vendorScan.movementCode || null,
        businessUnit: vendorScan.businessUnit || null,
        supportTeamMember: vendorScan.catMgrCode || null,
        apVendorNumber: vendorScan.apVendorNumber || null,
        addressSequence: vendorScan.addressSequence,
        entryType: vendorScan.entryType || null,
        invoiceDescription: vendorScan.invoiceDescription || null,
        internalVendorCode: "",
      };
    }

    if (enabled.internalScan) {
      if (!internalScan.amount || internalScan.amount <= 0) {
        setTopLevelError(
          "Internal Scanback is enabled but no amount was entered."
        );
        return null;
      }
      payload.internalScan = {
        zoneNumber: 0,
        amount: internalScan.amount,
        movementCode: internalScan.movementCode || "",
        internalVendorCode: "INTERNAL",
      };
    }

    if (enabled.promoPrice) {
      const p = promoPrice;
      const anyVal =
        p.price !== null ||
        p.amountOff !== null ||
        p.percentOff !== null ||
        !!p.specialPricing;
      if (!anyVal) {
        setTopLevelError(
          "Promo Price section is enabled but no fields were entered."
        );
        return null;
      }
      payload.promoPrice = {
        zoneNumber: 0,
        multiUnitCount: p.multiUnitCount,
        price: p.price,
        amountOff: p.amountOff,
        percentOff: p.percentOff,
        specialPricing: p.specialPricing || null,
      };
    }

    if (enabled.itemAttributes) {
      const ia = itemAttributes;
      const anyVal =
        ia.projectedUnits !== null ||
        ia.projectedUnitsUom !== "" ||
        ia.excludeAdPlan !== "" ||
        ia.excludeStoreAdPlan !== "";
      if (!anyVal) {
        setTopLevelError(
          "Item Attributes section is enabled but no fields were entered."
        );
        return null;
      }
      payload.itemAttributes = {
        projectedUnits:
          ia.projectedUnits !== null ? ia.projectedUnits : undefined,
        projectedUnitsUom: ia.projectedUnitsUom || undefined,
        excludeAdPlan:
          ia.excludeAdPlan === "" ? undefined : ia.excludeAdPlan === "Yes",
        excludeStoreAdPlan:
          ia.excludeStoreAdPlan === ""
            ? undefined
            : ia.excludeStoreAdPlan === "Yes",
      };
    }

    // Apply scope
    const scope: ApplyScope = { mode: scopeMode };
    if (scopeMode === "specific") {
      scope.zones = Array.from(pickedZones);
      scope.overrides = Array.from(pickedOverrides).map((v) => {
        const [z, o] = v.split(":").map(Number);
        return { zoneNumber: z, overrideNumber: o };
      });
      scope.stores = Array.from(pickedStores);
      scope.promoGroups = Array.from(pickedGroups);
      const anyScope =
        (scope.zones?.length ?? 0) > 0 ||
        (scope.overrides?.length ?? 0) > 0 ||
        (scope.stores?.length ?? 0) > 0 ||
        (scope.promoGroups?.length ?? 0) > 0;
      if (!anyScope) {
        setTopLevelError(
          'Apply Scope is set to "specific" but no zones, overrides, stores, or promo groups were picked.'
        );
        return null;
      }
    }
    payload.applyScope = scope;

    return payload;
  };

  const onApply = async () => {
    setTopLevelError(null);
    setResult(null);
    if (selectedIds.length === 0) {
      setTopLevelError(
        "No items selected. Go back to the items grid and pick at least one row."
      );
      return;
    }
    if (!hasAnySectionEnabled) {
      setTopLevelError(
        "Enable at least one section and enter values to apply."
      );
      return;
    }
    const payload = buildPayload();
    if (!payload) return;

    try {
      const r = await bulkEdit.mutateAsync({ ...payload, itemLookup });
      setResult(r);
      if (r.failed === 0) {
        setTimeout(() => navigate(`/dm/deals/${dealId}/items`), 600);
      }
    } catch (err) {
      setTopLevelError(
        err instanceof Error ? err.message : "Bulk edit failed unexpectedly."
      );
    }
  };

  const onReset = () => {
    setEnabled({
      allowance: false,
      shipperDates: false,
      vendorScan: false,
      internalScan: false,
      promoPrice: false,
      itemAttributes: false,
    });
    setAllowance({
      offInvoiceAmount: null,
      billBackAmount: null,
      countRecountAmount: null,
      passThruAmount: null,
      reflectAmount: null,
    });
    setShipperDates({
      buyOrderFirst: null,
      buyOrderLast: null,
      firstShip: null,
      lastShip: null,
    });
    setVendorScan({
      amount: null,
      businessUnit: "",
      movementCode: "",
      catMgrCode: "",
      entryType: "",
      apVendorNumber: "",
      addressSequence: null,
      invoiceDescription: "",
      setAsDefault: true,
    });
    setInternalScan({ amount: null, movementCode: "" });
    setPromoPrice({
      multiUnitCount: null,
      price: null,
      amountOff: null,
      percentOff: null,
      specialPricing: "",
    });
    setItemAttributes({
      projectedUnits: null,
      projectedUnitsUom: "",
      excludeAdPlan: "",
      excludeStoreAdPlan: "",
    });
    setScopeMode("all");
    setPickedZones(new Set());
    setPickedOverrides(new Set());
    setPickedStores(new Set());
    setPickedGroups(new Set());
    setTopLevelError(null);
    setResult(null);
  };

  const onCancel = () => navigate(`/dm/deals/${dealId}/items`);

  // DropDownList helpers — Kendo wants `{ code, label }` objects with a
  // textField/dataItemKey pair; we adapt the API rows on the fly.
  const buUiData = useMemo(
    () => [PLACEHOLDER, ...(businessUnits.data ?? [])],
    [businessUnits.data]
  );
  const movementUiData = useMemo(
    () => [PLACEHOLDER, ...movementCodes],
    [movementCodes]
  );
  const cmUiData = useMemo(
    () =>
      [PLACEHOLDER, ...(catMgrs.data ?? []).map((c) => ({
        code: c.code,
        label: `${c.code} - ${c.name}`,
      }))],
    [catMgrs.data]
  );
  const entryUiData = useMemo(
    () => [PLACEHOLDER, ...(entryTypes.data ?? [])],
    [entryTypes.data]
  );
  const apVendorUiData = useMemo(
    () =>
      [PLACEHOLDER, ...(apVendors.data ?? []).map((v) => ({
        code: v.apVendorNumber,
        label: v.apVendorName
          ? `${v.apVendorNumber} - ${v.apVendorName}`
          : v.apVendorNumber,
      }))],
    [apVendors.data]
  );
  const addrSeqUiData = useMemo(
    () => [
      { code: 0, label: "(no change)" },
      ...(addressSequences.data ?? []).map((a) => ({
        code: a.addressSequence,
        label: a.displayText,
      })),
    ],
    [addressSequences.data]
  );
  const uomUiData = useMemo(
    () => [PLACEHOLDER, ...(uoms.data ?? [])],
    [uoms.data]
  );

  return (
    <div className={s.page}>
      <div className={s.breadcrumbRow}>
        <Link to={`/dm/deals/${dealId}/items`} className={s.backLink}>
          ← Back to items
        </Link>
        <div className={s.pill}>{itemsListLabel}</div>
      </div>

      {/* Header / selected items chip list ----------------------------- */}
      <section className={s.card}>
        <h2 className={s.sectionTitle}>{titleSuffix}</h2>
        {selectedIds.length === 0 ? (
          <p className={s.subText}>
            No items were passed in. Go back to the items grid and pick rows
            before opening this page.
          </p>
        ) : (
          <>
            <p className={s.subText}>
              The fields below will be applied to every selected item. Toggle
              each section's checkbox to enable that group of changes.
            </p>
            <div className={s.itemList}>
              {selectedItems.slice(0, 20).map((i) => (
                <span
                  key={i.dealItemId}
                  className={s.itemChip}
                  title={i.itemDescription ?? ""}
                >
                  #{i.dealItemId} · {i.retailItemNumber}
                  {i.itemDescription ? ` — ${i.itemDescription}` : ""}
                </span>
              ))}
              {selectedIds.length > 20 && (
                <span className={s.moreChip}>
                  and {selectedIds.length - 20} more
                </span>
              )}
              {selectedItems.length === 0 && (
                <span className={s.moreChip}>
                  Loading item descriptions…
                </span>
              )}
            </div>
          </>
        )}
      </section>

      {/* 1. Allowances ------------------------------------------------- */}
      <section className={s.card}>
        <label className={s.sectionToggleRow}>
          <input
            type="checkbox"
            checked={enabled.allowance}
            onChange={(e) =>
              setEnabled({ ...enabled, allowance: e.target.checked })
            }
          />
          <h2 className={s.sectionTitle} style={{ marginBottom: 0 }}>
            Allowances
          </h2>
        </label>
        <div className={s.formGridHalves}>
          <div className={s.formGrid}>
            <span className={s.fieldLabel}>Off-Invoice ($)</span>
            <NumericTextBox
              value={allowance.offInvoiceAmount}
              onChange={(e) =>
                setAllowance({
                  ...allowance,
                  offInvoiceAmount: e.value ?? null,
                })
              }
              format="c2"
              min={0}
              max={9999.99}
              disabled={!enabled.allowance}
            />
            <span className={s.fieldLabel}>Bill Back ($)</span>
            <NumericTextBox
              value={allowance.billBackAmount}
              onChange={(e) =>
                setAllowance({ ...allowance, billBackAmount: e.value ?? null })
              }
              format="c2"
              min={0}
              max={999.99}
              disabled={!enabled.allowance}
            />
            <span className={s.fieldLabel}>Count/Recount ($)</span>
            <NumericTextBox
              value={allowance.countRecountAmount}
              onChange={(e) =>
                setAllowance({
                  ...allowance,
                  countRecountAmount: e.value ?? null,
                })
              }
              format="c2"
              min={0}
              max={99999.99}
              disabled={!enabled.allowance}
            />
          </div>
          <div className={s.formGrid}>
            <span className={s.fieldLabel}>Pass-Through ($)</span>
            <NumericTextBox
              value={allowance.passThruAmount}
              onChange={(e) =>
                setAllowance({
                  ...allowance,
                  passThruAmount: e.value ?? null,
                })
              }
              format="c2"
              min={0}
              max={999.99}
              disabled={!enabled.allowance}
            />
            <span className={s.fieldLabel}>Reflect ($)</span>
            <NumericTextBox
              value={allowance.reflectAmount}
              onChange={(e) =>
                setAllowance({
                  ...allowance,
                  reflectAmount: e.value ?? null,
                })
              }
              format="c2"
              min={0}
              max={999.99}
              disabled={!enabled.allowance}
            />
          </div>
        </div>
      </section>

      {/* 2. Shipper Dates --------------------------------------------- */}
      <section className={s.card}>
        <label className={s.sectionToggleRow}>
          <input
            type="checkbox"
            checked={enabled.shipperDates}
            onChange={(e) =>
              setEnabled({ ...enabled, shipperDates: e.target.checked })
            }
          />
          <h2 className={s.sectionTitle} style={{ marginBottom: 0 }}>
            Shipper Dates{" "}
            <span style={{ fontWeight: 400, textTransform: "none" }}>
              (Only for Shipper Items and different from deal dates)
            </span>
          </h2>
        </label>
        <div className={s.formGridHalves}>
          <div className={s.formGrid}>
            <span className={s.fieldLabel}>Buy / Order First</span>
            <CleanDatePicker
              value={shipperDates.buyOrderFirst}
              onChange={(e) =>
                setShipperDates({ ...shipperDates, buyOrderFirst: e.value })
              }
              disabled={!enabled.shipperDates}
            />
            <span className={s.fieldLabel}>Buy / Order Last</span>
            <CleanDatePicker
              value={shipperDates.buyOrderLast}
              onChange={(e) =>
                setShipperDates({ ...shipperDates, buyOrderLast: e.value })
              }
              disabled={!enabled.shipperDates}
            />
          </div>
          <div className={s.formGrid}>
            <span className={s.fieldLabel}>First Ship</span>
            <CleanDatePicker
              value={shipperDates.firstShip}
              onChange={(e) =>
                setShipperDates({ ...shipperDates, firstShip: e.value })
              }
              disabled={!enabled.shipperDates}
            />
            <span className={s.fieldLabel}>Last Ship</span>
            <CleanDatePicker
              value={shipperDates.lastShip}
              onChange={(e) =>
                setShipperDates({ ...shipperDates, lastShip: e.value })
              }
              disabled={!enabled.shipperDates}
            />
          </div>
        </div>
      </section>

      {/* 3. Vendor Scanback ------------------------------------------- */}
      <section className={s.card}>
        <label className={s.sectionToggleRow}>
          <input
            type="checkbox"
            checked={enabled.vendorScan}
            onChange={(e) =>
              setEnabled({ ...enabled, vendorScan: e.target.checked })
            }
          />
          <h2 className={s.sectionTitle} style={{ marginBottom: 0 }}>
            Vendor Scanback
          </h2>
        </label>
        <div className={s.formGrid}>
          <span className={s.fieldLabel}>Scanback Amount ($)</span>
          <NumericTextBox
            value={vendorScan.amount}
            onChange={(e) =>
              setVendorScan({ ...vendorScan, amount: e.value ?? null })
            }
            format="c2"
            min={0.01}
            max={999.99}
            disabled={!enabled.vendorScan}
          />
          <span className={s.fieldLabel}>Business Unit</span>
          <DropDownList
            data={buUiData}
            textField="label"
            dataItemKey="code"
            value={
              buUiData.find((b) => b.code === vendorScan.businessUnit) ??
              PLACEHOLDER
            }
            onChange={(e) =>
              setVendorScan({
                ...vendorScan,
                businessUnit: (e.value?.code as string) ?? "",
              })
            }
            disabled={!enabled.vendorScan}
            style={{ width: "100%" }}
          />
          <span className={s.fieldLabel}>Movement</span>
          <DropDownList
            data={movementUiData}
            textField="label"
            dataItemKey="code"
            value={
              movementUiData.find((m) => m.code === vendorScan.movementCode) ??
              PLACEHOLDER
            }
            onChange={(e) =>
              setVendorScan({
                ...vendorScan,
                movementCode: (e.value?.code as string) ?? "",
              })
            }
            disabled={!enabled.vendorScan}
            style={{ width: "100%" }}
          />
          <span className={s.fieldLabel}>Category Manager</span>
          <DropDownList
            data={cmUiData}
            textField="label"
            dataItemKey="code"
            value={
              cmUiData.find((c) => c.code === vendorScan.catMgrCode) ??
              PLACEHOLDER
            }
            onChange={(e) =>
              setVendorScan({
                ...vendorScan,
                catMgrCode: (e.value?.code as string) ?? "",
              })
            }
            disabled={!enabled.vendorScan}
            style={{ width: "100%" }}
          />
          <span className={s.fieldLabel}>Entry Type</span>
          <DropDownList
            data={entryUiData}
            textField="label"
            dataItemKey="code"
            value={
              entryUiData.find((et) => et.code === vendorScan.entryType) ??
              PLACEHOLDER
            }
            onChange={(e) =>
              setVendorScan({
                ...vendorScan,
                entryType: (e.value?.code as string) ?? "",
              })
            }
            disabled={!enabled.vendorScan}
            style={{ width: "100%" }}
          />
          <span className={s.fieldLabel}>A/P Vendor</span>
          <DropDownList
            data={apVendorUiData}
            textField="label"
            dataItemKey="code"
            value={
              apVendorUiData.find(
                (v) => v.code === vendorScan.apVendorNumber
              ) ?? PLACEHOLDER
            }
            onChange={(e) =>
              setVendorScan({
                ...vendorScan,
                apVendorNumber: (e.value?.code as string) ?? "",
                // Reset address-seq when AP vendor changes; the cascade
                // mirrors the legacy AutoPostBack behavior.
                addressSequence: null,
              })
            }
            disabled={!enabled.vendorScan}
            style={{ width: "100%" }}
          />
          <span className={s.fieldLabel}>Address Seq</span>
          <DropDownList
            data={addrSeqUiData}
            textField="label"
            dataItemKey="code"
            value={
              addrSeqUiData.find(
                (a) => a.code === (vendorScan.addressSequence ?? 0)
              ) ?? addrSeqUiData[0]
            }
            onChange={(e) =>
              setVendorScan({
                ...vendorScan,
                addressSequence:
                  (e.value?.code as number) === 0
                    ? null
                    : ((e.value?.code as number) ?? null),
              })
            }
            disabled={!enabled.vendorScan || !vendorScan.apVendorNumber}
            style={{ width: "100%" }}
          />
          <span className={s.fieldLabel}>Invoice Description</span>
          <Input
            value={vendorScan.invoiceDescription}
            onChange={(e) =>
              setVendorScan({
                ...vendorScan,
                invoiceDescription: e.value as string,
              })
            }
            disabled={!enabled.vendorScan}
            maxLength={200}
            style={{ width: "100%" }}
          />
          <span />
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.4rem",
              fontSize: "0.85rem",
              color: "#334155",
            }}
          >
            <input
              type="checkbox"
              checked={vendorScan.setAsDefault}
              onChange={(e) =>
                setVendorScan({
                  ...vendorScan,
                  setAsDefault: e.target.checked,
                })
              }
              disabled={!enabled.vendorScan}
            />
            Default — by clicking the Default checkbox, this allocation will
            become the default allocation for future scanbacks.
          </label>
        </div>
      </section>

      {/* 4. Internal Scanback ----------------------------------------- */}
      <section className={s.card}>
        <label className={s.sectionToggleRow}>
          <input
            type="checkbox"
            checked={enabled.internalScan}
            onChange={(e) =>
              setEnabled({ ...enabled, internalScan: e.target.checked })
            }
          />
          <h2 className={s.sectionTitle} style={{ marginBottom: 0 }}>
            Internal Scanback
          </h2>
        </label>
        <div className={s.formGrid}>
          <span className={s.fieldLabel}>Scanback Amount ($)</span>
          <NumericTextBox
            value={internalScan.amount}
            onChange={(e) =>
              setInternalScan({ ...internalScan, amount: e.value ?? null })
            }
            format="c2"
            min={0.01}
            max={999.99}
            disabled={!enabled.internalScan}
          />
          <span className={s.fieldLabel}>Movement</span>
          <DropDownList
            data={movementUiData}
            textField="label"
            dataItemKey="code"
            value={
              movementUiData.find(
                (m) => m.code === internalScan.movementCode
              ) ?? PLACEHOLDER
            }
            onChange={(e) =>
              setInternalScan({
                ...internalScan,
                movementCode: (e.value?.code as string) ?? "",
              })
            }
            disabled={!enabled.internalScan}
            style={{ width: "100%" }}
          />
        </div>
      </section>

      {/* 5. Promo Price ----------------------------------------------- */}
      <section className={s.card}>
        <label className={s.sectionToggleRow}>
          <input
            type="checkbox"
            checked={enabled.promoPrice}
            onChange={(e) =>
              setEnabled({ ...enabled, promoPrice: e.target.checked })
            }
          />
          <h2 className={s.sectionTitle} style={{ marginBottom: 0 }}>
            Promo Price
          </h2>
        </label>
        {!enabled.promoPrice && (
          <p className={s.subText} style={{ marginBottom: 0 }}>
            Enable this section to update promotional pricing.
          </p>
        )}
        {enabled.promoPrice && (
          <div className={s.formGridHalves}>
            <div className={s.formGrid}>
              <span className={s.fieldLabel}>Promo Qty</span>
              <NumericTextBox
                value={promoPrice.multiUnitCount}
                onChange={(e) =>
                  setPromoPrice({
                    ...promoPrice,
                    multiUnitCount: e.value ?? null,
                  })
                }
                min={1}
                max={99}
                format="n0"
              />
              <span className={s.fieldLabel}>Promo Price ($)</span>
              <NumericTextBox
                value={promoPrice.price}
                onChange={(e) =>
                  setPromoPrice({ ...promoPrice, price: e.value ?? null })
                }
                format="c2"
                min={0.01}
                max={9999.99}
              />
              <span className={s.fieldLabel}>Special Pricing</span>
              <DropDownList
                data={[...SPECIAL_PRICING_OPTIONS]}
                value={promoPrice.specialPricing}
                onChange={(e) =>
                  setPromoPrice({
                    ...promoPrice,
                    specialPricing: (e.value as string) ?? "",
                  })
                }
                style={{ width: "100%" }}
              />
            </div>
            <div className={s.formGrid}>
              <span className={s.fieldLabel}>Promo $ Off</span>
              <NumericTextBox
                value={promoPrice.amountOff}
                onChange={(e) =>
                  setPromoPrice({ ...promoPrice, amountOff: e.value ?? null })
                }
                format="c2"
                min={0.01}
                max={9999.99}
              />
              <span className={s.fieldLabel}>Promo % Off</span>
              <NumericTextBox
                value={promoPrice.percentOff}
                onChange={(e) =>
                  setPromoPrice({
                    ...promoPrice,
                    percentOff: e.value ?? null,
                  })
                }
                min={1}
                max={99.99}
                format="n2"
              />
            </div>
          </div>
        )}
      </section>

      {/* 6. Item Attributes ------------------------------------------- */}
      <section className={s.card}>
        <label className={s.sectionToggleRow}>
          <input
            type="checkbox"
            checked={enabled.itemAttributes}
            onChange={(e) =>
              setEnabled({ ...enabled, itemAttributes: e.target.checked })
            }
          />
          <h2 className={s.sectionTitle} style={{ marginBottom: 0 }}>
            Item Attributes
          </h2>
        </label>
        <div className={s.formGridHalves}>
          <div className={s.formGrid}>
            <span className={s.fieldLabel}>Projected Units</span>
            <NumericTextBox
              value={itemAttributes.projectedUnits}
              onChange={(e) =>
                setItemAttributes({
                  ...itemAttributes,
                  projectedUnits: e.value ?? null,
                })
              }
              min={1}
              max={99999}
              format="n0"
              disabled={!enabled.itemAttributes}
            />
            <span className={s.fieldLabel}>UOM</span>
            <DropDownList
              data={uomUiData}
              textField="label"
              dataItemKey="code"
              value={
                uomUiData.find(
                  (u) => u.code === itemAttributes.projectedUnitsUom
                ) ?? PLACEHOLDER
              }
              onChange={(e) =>
                setItemAttributes({
                  ...itemAttributes,
                  projectedUnitsUom: (e.value?.code as string) ?? "",
                })
              }
              disabled={!enabled.itemAttributes}
              style={{ width: "100%" }}
            />
          </div>
          <div className={s.formGrid}>
            <span className={s.fieldLabel}>Exclude from Ad Plan</span>
            <DropDownList
              data={[...YES_NO_OPTIONS]}
              value={itemAttributes.excludeAdPlan}
              onChange={(e) =>
                setItemAttributes({
                  ...itemAttributes,
                  excludeAdPlan: (e.value as string) ?? "",
                })
              }
              disabled={!enabled.itemAttributes}
              style={{ width: "100%" }}
            />
            <span className={s.fieldLabel}>
              Exclude Item Code from Store Ad Plan
            </span>
            <DropDownList
              data={[...YES_NO_OPTIONS]}
              value={itemAttributes.excludeStoreAdPlan}
              onChange={(e) =>
                setItemAttributes({
                  ...itemAttributes,
                  excludeStoreAdPlan: (e.value as string) ?? "",
                })
              }
              disabled={!enabled.itemAttributes}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </section>

      {/* 7. Apply Scope ----------------------------------------------- */}
      <section className={s.card}>
        <h2 className={s.sectionTitle}>Apply Scope</h2>
        <div className={s.radioRow}>
          <label className={s.radioLabel}>
            <input
              type="radio"
              name="applyScope"
              checked={scopeMode === "all"}
              onChange={() => setScopeMode("all")}
            />
            Add / Update All
          </label>
          <label className={s.radioLabel}>
            <input
              type="radio"
              name="applyScope"
              checked={scopeMode === "specific"}
              onChange={() => setScopeMode("specific")}
            />
            Add / Update for specific
          </label>
        </div>
        {scopeMode === "specific" && (
          <>
            {scopeOptions.loading && (
              <p className={s.subText}>Loading scope options…</p>
            )}
            {scopeOptions.error && (
              <p className={s.noticeError}>{scopeOptions.error}</p>
            )}
            {!scopeOptions.loading && !scopeOptions.error && (
              <div className={s.specificGrid}>
                <div>
                  <div className={s.fieldLabel} style={{ textAlign: "left" }}>
                    Zones
                  </div>
                  <select
                    multiple
                    className={s.listbox}
                    value={Array.from(pickedZones).map(String)}
                    onChange={(e) => {
                      const next = new Set<number>();
                      Array.from(e.target.selectedOptions).forEach((o) =>
                        next.add(Number(o.value))
                      );
                      setPickedZones(next);
                    }}
                  >
                    {scopeOptions.zones.map((z) => (
                      <option key={z} value={z}>
                        Zone {z}
                      </option>
                    ))}
                    {scopeOptions.zones.length === 0 && (
                      <option disabled>(no zones on selected items)</option>
                    )}
                  </select>
                </div>
                <div>
                  <div className={s.fieldLabel} style={{ textAlign: "left" }}>
                    Overrides
                  </div>
                  <select
                    multiple
                    className={s.listbox}
                    value={Array.from(pickedOverrides)}
                    onChange={(e) => {
                      const next = new Set<string>();
                      Array.from(e.target.selectedOptions).forEach((o) =>
                        next.add(o.value)
                      );
                      setPickedOverrides(next);
                    }}
                  >
                    {scopeOptions.overrides.map((o) => {
                      const key = `${o.zoneNumber}:${o.overrideNumber}`;
                      return (
                        <option key={key} value={key}>
                          Zone {o.zoneNumber} / Override {o.overrideNumber}
                        </option>
                      );
                    })}
                    {scopeOptions.overrides.length === 0 && (
                      <option disabled>(no overrides)</option>
                    )}
                  </select>
                </div>
                <div>
                  <div className={s.fieldLabel} style={{ textAlign: "left" }}>
                    Stores
                  </div>
                  <select
                    multiple
                    className={s.listbox}
                    value={Array.from(pickedStores).map(String)}
                    onChange={(e) => {
                      const next = new Set<number>();
                      Array.from(e.target.selectedOptions).forEach((o) =>
                        next.add(Number(o.value))
                      );
                      setPickedStores(next);
                    }}
                  >
                    {scopeOptions.stores.map((st) => (
                      <option key={st} value={st}>
                        Store {st}
                      </option>
                    ))}
                    {scopeOptions.stores.length === 0 && (
                      <option disabled>(no stores)</option>
                    )}
                  </select>
                </div>
                <div>
                  <div className={s.fieldLabel} style={{ textAlign: "left" }}>
                    Promo Groups
                  </div>
                  <select
                    multiple
                    className={s.listbox}
                    value={Array.from(pickedGroups).map(String)}
                    onChange={(e) => {
                      const next = new Set<number>();
                      Array.from(e.target.selectedOptions).forEach((o) =>
                        next.add(Number(o.value))
                      );
                      setPickedGroups(next);
                    }}
                  >
                    {scopeOptions.promoGroups.map((g) => (
                      <option key={g} value={g}>
                        Promo Group {g}
                      </option>
                    ))}
                    {scopeOptions.promoGroups.length === 0 && (
                      <option disabled>(no promo groups)</option>
                    )}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* Status banners ---------------------------------------------- */}
      {topLevelError && <div className={s.noticeError}>{topLevelError}</div>}
      {result && (
        <div className={result.failed > 0 ? s.noticeError : s.noticeInfo}>
          {result.failed === 0
            ? `Applied to ${result.succeeded} of ${result.total} items. Redirecting…`
            : `Applied to ${result.succeeded} of ${result.total} items. ${result.failed} failed.`}
          {result.errors.length > 0 && (
            <div className={s.errorList}>
              {result.errors.map((e, idx) => (
                <div key={`${e.dealItemId}-${e.section}-${idx}`}>
                  Item {e.dealItemId} · {e.section}: {e.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sticky action bar ------------------------------------------- */}
      <div className={s.stickyBar}>
        <button
          type="button"
          className={s.resetLink}
          onClick={onReset}
          disabled={bulkEdit.isPending}
        >
          Reset
        </button>
        <Button onClick={onCancel} disabled={bulkEdit.isPending}>
          Cancel
        </Button>
        <Button
          themeColor="primary"
          onClick={onApply}
          disabled={bulkEdit.isPending || selectedIds.length === 0}
        >
          {bulkEdit.isPending ? "Updating…" : "Update"}
        </Button>
      </div>
    </div>
  );
};

export default BulkEditItems;
